import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const AdminPanel = ({ theme }) => {
  // State for views and authentication
  const [view, setView] = useState('login');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [token, setToken] = useState(null);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [status, setStatus] = useState('');
  const [error, setError] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());

  // State for admin features
  const [newsTitle, setNewsTitle] = useState('');
  const [newsDescription, setNewsDescription] = useState('');
  const [newsItems, setNewsItems] = useState('');
  const [jsonTools, setJsonTools] = useState(''); // JSON editor content (hierarchical)
  const [activeTab, setActiveTab] = useState('news');

  const BASE_URL = 'https://api.spookytools.com';

  // Clock effect
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Login function
  const handleLogin = async () => {
    setStatus('Logging in...');
    setError(null);

    try {
      const response = await fetch(`${BASE_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });

      if (!response.ok) {
        throw new Error('Login failed');
      }

      const data = await response.json();
      if (!data.token) {
        throw new Error('No token received');
      }

      setToken(data.token);
      setIsAuthenticated(true);
      setView('dashboard');
      setStatus('Login successful!');
      setUsername('');
      setPassword('');

      // Load initial news and tools data
      await fetchNews();
      await fetchTools();
    } catch (err) {
      setError('Invalid username or password');
      setStatus('');
    }
  };

  // Logout function
  const handleLogout = () => {
    setIsAuthenticated(false);
    setToken(null);
    setView('login');
    setStatus('');
    setError(null);
    setActiveTab('news');
    setNewsTitle('');
    setNewsDescription('');
    setNewsItems('');
    setJsonTools('');
  };

  // Fetch news (public)
  const fetchNews = async () => {
    try {
      const response = await fetch(`${BASE_URL}/api/status`);
      if (!response.ok) return;

      const data = await response.json();
      if (data.news) {
        setNewsTitle(data.news.title || '');
        setNewsDescription(data.news.description || '');
        setNewsItems(data.news.items ? data.news.items.join('\n') : '');
      }
    } catch (err) {
      console.error('Failed to fetch news:', err);
    }
  };

  // Update news (authenticated)
  const handleNewsSubmit = async (e) => {
    e.preventDefault();
    if (!isAuthenticated || !token) {
      setError('Not authenticated');
      return;
    }

    try {
      const newsConfig = {
        title: newsTitle,
        description: newsDescription,
        items: newsItems.split('\n').filter(item => item.trim()),
      };

      const response = await fetch(`${BASE_URL}/api/status`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(newsConfig),
      });

      if (!response.ok) {
        throw new Error('Failed to update news');
      }

      setStatus('News updated successfully!');
      setTimeout(() => setStatus(''), 3000);
    } catch (err) {
      setError('Failed to update news: ' + err.message);
    }
  };

  // Fetch tools (public)
  const fetchTools = async () => {
    try {
      const response = await fetch(`${BASE_URL}/api/tools`);
      if (!response.ok) return;

      const data = await response.json();
      if (data.tools) {
        // Store hierarchical data for JSON editor
        setJsonTools(JSON.stringify(data.tools, null, 2));
      }
    } catch (err) {
      console.error('Failed to fetch tools:', err);
    }
  };

  // Update tools (authenticated)
  const handleToolsSubmit = async (e) => {
    e.preventDefault();
    if (!isAuthenticated || !token) {
      setError('Not authenticated');
      return;
    }

    try {
      // Validate JSON
      let parsedTools;
      try {
        parsedTools = JSON.parse(jsonTools);
        if (!Array.isArray(parsedTools)) {
          throw new Error('JSON must be an array of tools');
        }
      } catch (jsonErr) {
        setError('Invalid JSON: ' + jsonErr.message);
        return;
      }

      const payload = { tools: parsedTools };

      const response = await fetch(`${BASE_URL}/api/tools`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error('Failed to update tools');
      }

      setStatus('Tools updated successfully!');
      setTimeout(() => setStatus(''), 3000);
    } catch (err) {
      setError('Failed to update tools: ' + err.message);
    }
  };

  return (
    <div className="min-h-screen w-full overflow-hidden flex justify-center items-center bg-gray-900">
      <div className="absolute inset-0 backdrop-blur-sm z-0"></div>

      {/* Header */}
      <div className="absolute top-0 left-0 right-0 flex justify-between items-center p-2 z-30">
        <div className="flex items-center space-x-1 bg-black bg-opacity-70 border-l-2 border-orange-600 px-2 py-1">
          <span className="text-xs text-white font-bold uppercase">
            {isAuthenticated ? 'Admin Dashboard' : 'Admin Login'}
          </span>
        </div>
        <div className="text-gray-400 text-xs bg-black bg-opacity-70 px-2 py-1">
          {currentTime.toLocaleString()}
        </div>
      </div>

      {/* Main Content */}
      <div className="w-full max-w-2xl bg-black bg-opacity-80 border-l-2 border-orange-600 p-4 sm:p-6 m-4 z-10">
        <div className="relative w-full max-w-md mx-auto mb-6">
          <div className="absolute -left-2 top-1/2 h-px w-6 bg-orange-600 hidden sm:block"></div>
          <div className="absolute -left-4 top-1/2 h-3 w-3 bg-white hidden sm:block"></div>
          <h2 className="text-2xl sm:text-3xl font-mono text-white text-center">
            {isAuthenticated ? 'ADMIN DASHBOARD' : 'ADMIN LOGIN'}
          </h2>
          <div className="absolute -right-2 top-1/2 h-px w-6 bg-orange-600 hidden sm:block"></div>
        </div>

        <div className="w-full h-px bg-orange-600 mb-4 opacity-50"></div>

        {/* Login View */}
        {view === 'login' && !isAuthenticated && (
          <div>
            <p className="text-gray-300 text-center mb-6 text-sm sm:text-base">
              Enter your admin credentials to access the dashboard
            </p>
            <div className="space-y-4">
              <input
                type="text"
                placeholder="Username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full bg-gray-800 text-white border border-gray-600 rounded p-2 font-mono text-sm focus:outline-none focus:border-orange-600"
              />
              <input
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-gray-800 text-white border border-gray-600 rounded p-2 font-mono text-sm focus:outline-none focus:border-orange-600"
              />
              <button
                onClick={handleLogin}
                className="w-full bg-orange-600 text-black font-bold py-2 rounded transition-all duration-300 hover:bg-orange-500 hover:shadow-[0_0_15px_rgba(249,115,22,0.6)]"
              >
                LOGIN
              </button>
            </div>
            {status && (
              <div className="mt-4 text-center text-sm text-gray-300 bg-black bg-opacity-50 p-2 border-l border-orange-600">
                {status}
              </div>
            )}
            {error && (
              <div className="mt-4 text-center text-red-500 bg-black bg-opacity-50 p-2">
                {error}
              </div>
            )}
          </div>
        )}

        {/* Dashboard View */}
        {view === 'dashboard' && isAuthenticated && (
          <div>
            <div className="flex justify-between mb-4">
              <button
                onClick={() => setActiveTab('news')}
                className={`flex-1 py-2 mx-1 rounded font-mono text-sm ${activeTab === 'news' ? 'bg-orange-600 text-black' : 'bg-gray-800 text-white hover:bg-gray-700'}`}
              >
                News
              </button>
              <button
                onClick={() => setActiveTab('tools')}
                className={`flex-1 py-2 mx-1 rounded font-mono text-sm ${activeTab === 'tools' ? 'bg-orange-600 text-black' : 'bg-gray-800 text-white hover:bg-gray-700'}`}
              >
                Tools
              </button>
              <button
                onClick={() => setActiveTab('settings')}
                className={`flex-1 py-2 mx-1 rounded font-mono text-sm ${activeTab === 'settings' ? 'bg-orange-600 text-black' : 'bg-gray-800 text-white hover:bg-gray-700'}`}
              >
                Settings
              </button>
            </div>

            {/* News Tab */}
            {activeTab === 'news' && (
              <form onSubmit={handleNewsSubmit} className="space-y-4">
                <input
                  value={newsTitle}
                  onChange={(e) => setNewsTitle(e.target.value)}
                  placeholder="News Title"
                  className="w-full bg-gray-800 text-white border border-gray-600 rounded p-2 font-mono text-sm focus:outline-none focus:border-orange-600"
                />
                <textarea
                  value={newsDescription}
                  onChange={(e) => setNewsDescription(e.target.value)}
                  placeholder="News Description"
                  className="w-full h-20 bg-gray-800 text-white border border-gray-600 rounded p-2 font-mono text-sm focus:outline-none focus:border-orange-600"
                />
                <textarea
                  value={newsItems}
                  onChange={(e) => setNewsItems(e.target.value)}
                  placeholder="News Items (one per line)"
                  className="w-full h-32 bg-gray-800 text-white border border-gray-600 rounded p-2 font-mono text-sm focus:outline-none focus:border-orange-600"
                />
                <button
                  type="submit"
                  className="w-full bg-orange-600 text-black font-bold py-2 rounded transition-all duration-300 hover:bg-orange-500 hover:shadow-[0_0_15px_rgba(249,115,22,0.6)]"
                >
                  Update News
                </button>
              </form>
            )}

            {/* Tools Tab */}
            {activeTab === 'tools' && (
              <form onSubmit={handleToolsSubmit} className="space-y-4">
                <textarea
                  value={jsonTools}
                  onChange={(e) => setJsonTools(e.target.value)}
                  placeholder="Edit tools JSON here (must be an array of tools with optional subTools)"
                  className="w-full h-96 bg-gray-800 text-white border border-gray-600 rounded p-2 font-mono text-sm focus:outline-none focus:border-orange-600"
                />
                <button
                  type="submit"
                  className="w-full bg-orange-600 text-black font-bold py-2 rounded transition-all duration-300 hover:bg-orange-500 hover:shadow-[0_0_15px_rgba(249,115,22,0.6)]"
                >
                  Update Tools
                </button>
              </form>
            )}

            {/* Settings Tab (Placeholder) */}
            {activeTab === 'settings' && (
              <div className="text-gray-300 text-center">
                <p>Settings coming soon...</p>
              </div>
            )}

            {status && (
              <div className="mt-4 text-center text-sm text-gray-300 bg-black bg-opacity-50 p-2 border-l border-orange-600">
                {status}
              </div>
            )}
            {error && (
              <div className="mt-4 text-center text-red-500 bg-black bg-opacity-50 p-2">
                {error}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Navigation Links */}
      <Link
        to="/"
        className="fixed bottom-4 left-4 z-20 flex items-center bg-black bg-opacity-70 border-l-2 border-orange-600 px-3 py-2 group hover:bg-orange-500 transition-all duration-200"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-4 w-4 mr-2 text-white group-hover:text-black transition-all duration-200"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        <span className="text-white text-sm font-bold tracking-wider uppercase group-hover:text-black transition-all duration-200">
          Back to Main Site
        </span>
      </Link>

      {isAuthenticated && (
        <button
          onClick={handleLogout}
          className="fixed bottom-4 right-4 z-20 bg-red-600 text-white font-bold px-3 py-2 rounded transition-all duration-300 hover:bg-red-500 hover:shadow-[0_0_15px_rgba(220,38,38,0.6)]"
        >
          Logout
        </button>
      )}

      <div className="fixed bottom-2 right-2 text-gray-400 text-[10px] bg-black bg-opacity-70 p-1 rounded z-30">
        Connection Status: <span className="text-green-500">Connected</span>
      </div>
    </div>
  );
};

export default AdminPanel;